import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import AuthContext from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import {
  FaPlus,
  FaArrowLeft,
  FaTrash,
  FaEdit,
  FaFileUpload,
  FaUsers,
  FaCalendarAlt,
  FaSearch,
} from "react-icons/fa";
import Select from "react-select";
import ConfirmationModal from "../components/ConfirmationModal";
import "./GerenciarEditais.css";

// Componente para a lista de editais (permanece o mesmo)
const EditalList = () => {
  const [editais, setEditais] = useState([]);
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  const [modalState, setModalState] = useState({
    isOpen: false,
    editalId: null,
  });
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const { user } = useContext(AuthContext);

  const getStatusPillClass = (status) => {
    if (status === "Aberto") return "pill-success";
    if (status === "Em Análise") return "pill-info";
    return "pill-muted";
  };

  const getStatusCardClass = (status) => {
    if (status === "Aberto") return "edital-card--success";
    if (status === "Em Análise") return "edital-card--info";
    return "edital-card--muted";
  };

  const formatDateTime = (value) => {
    if (!value) return "N/D";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "N/D";
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(dt);
  };

  const getEditUrl = (editalId) => {
    if (user?.perfil_id === 1) {
      // Admin
      return `/admin/editais/editar/${editalId}`;
    }
    // Controlador
    return `/controlador/editais/editar/${editalId}`;
  };

  const fetchEditais = useCallback(async () => {
    try {
      const { data } = await api.get("/editais");
      setEditais(data);
    } catch (error) {
      addToast("Erro ao buscar editais.", "error");
    }
  }, [addToast]);

  useEffect(() => {
    fetchEditais();
  }, [fetchEditais]);

  const editaisFiltrados = React.useMemo(() => {
    const termo = termoPesquisa.trim().toLowerCase();

    return editais
      .filter((edital) => {
        if (statusFiltro && edital.status !== statusFiltro) return false;

        if (!termo) return true;

        const title = `${edital.nome_edital} - ${edital.numero_edital}/${edital.ano_edital}`.toLowerCase();
        return title.includes(termo);
      })
      .sort((a, b) => {
        const aDate = a.criado_em ? new Date(a.criado_em).getTime() : 0;
        const bDate = b.criado_em ? new Date(b.criado_em).getTime() : 0;
        return bDate - aDate;
      });
  }, [editais, statusFiltro, termoPesquisa]);

  const handleDelete = (id) => {
    setModalState({ isOpen: true, editalId: id });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/editais/${modalState.editalId}`);
      addToast("Edital excluído com sucesso!", "success");
      fetchEditais();
    } catch (error) {
      addToast("Erro ao excluir edital.", "error");
    } finally {
      setModalState({ isOpen: false, editalId: null });
    }
  };

  return (
    <div className="edital-page">
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, editalId: null })}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este edital? Esta ação removerá o edital, seus cargos, inscrições e todos os documentos associados."
      />
      <PageHeader title="Gerenciar Editais">
        <button
          onClick={() => {
            const path =
              user?.perfil_id === 1
                ? "/admin/editais/novo"
                : "/controlador/editais/novo";
            navigate(path);
          }}
        >
          <FaPlus />
          <span>Criar Novo Edital</span>
        </button>
      </PageHeader>
      <Card>
        <div className="filters-bar">
          <div className="input-with-icon">
            <FaSearch />
            <input
              type="search"
              placeholder="Buscar edital por nome, número ou ano"
              value={termoPesquisa}
              onChange={(e) => setTermoPesquisa(e.target.value)}
            />
          </div>
          <select
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value)}
            aria-label="Filtrar por status"
          >
            <option value="">Todos os status</option>
            <option value="Aberto">Aberto</option>
            <option value="Em Análise">Em Análise</option>
            <option value="Finalizado">Finalizado</option>
          </select>
          <div className="spacer" />
          <span className="pill">{editaisFiltrados.length} editais</span>
        </div>

        {editaisFiltrados.length > 0 ? (
          <div className="edital-grid">
            {editaisFiltrados.map((edital) => (
              <div
                key={edital.id}
                className={`edital-card ${getStatusCardClass(edital.status)}`}
              >
                <div className="edital-card-header">
                  <div className="edital-card-title">
                    <strong>
                      {edital.nome_edital} - {edital.numero_edital}/
                      {edital.ano_edital}
                    </strong>
                    <span className="edital-id">ID {edital.id}</span>
                  </div>
                  <span className={`pill ${getStatusPillClass(edital.status)}`}>
                    {edital.status}
                  </span>
                </div>

                <div className="edital-meta">
                  <div className="edital-meta-row">
                    <FaCalendarAlt />
                    <strong>Inscrições:</strong>
                    <span>
                      {formatDateTime(edital.data_inicio_inscricao)} — {formatDateTime(edital.data_fim_inscricao)}
                    </span>
                  </div>
                  <div className="edital-meta-row">
                    <FaCalendarAlt />
                    <strong>Recursos:</strong>
                    <span>
                      {formatDateTime(edital.data_inicio_recurso)} — {formatDateTime(edital.data_fim_recurso)}
                    </span>
                  </div>
                </div>

                <div className="edital-card-actions">
                  <Link
                    to={getEditUrl(edital.id)}
                    className="btn-modern btn-add btn-compact"
                    title="Editar edital"
                  >
                    <FaEdit />
                    <span>Editar</span>
                  </Link>
                  <Link
                    to={`/controlador/edital/${edital.id}/candidatos`}
                    className="btn-modern btn-file btn-compact"
                    title="Ver candidatos inscritos"
                  >
                    <FaUsers />
                    <span>Candidatos</span>
                  </Link>
                  <button
                    onClick={() => handleDelete(edital.id)}
                    className="btn-modern btn-danger btn-compact"
                    title="Excluir edital"
                  >
                    <FaTrash />
                    <span>Excluir</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted-text">Nenhum edital cadastrado.</p>
        )}
      </Card>
    </div>
  );
};

// Componente para o formulário de criação/edição de edital (Refatorado)
const EditalForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    nome_edital: "",
    numero_edital: "",
    ano_edital: "",
    descricao: "",
    data_inicio_inscricao: "",
    data_fim_inscricao: "",
    data_inicio_recurso: "",
    data_fim_recurso: "",
    uma_inscricao_por_cpf: false,
    status: "Aberto",
  });
  const [todosCargos, setTodosCargos] = useState([]);
  const [cargosSelecionados, setCargosSelecionados] = useState([]);
  const [controladores, setControladores] = useState([]);
  const [controladorSelecionado, setControladorSelecionado] = useState(null);
  const [anexos, setAnexos] = useState([]);
  const [anexoFile, setAnexoFile] = useState(null);
  const [loadingForm, setLoadingForm] = useState(true);
  const [saving, setSaving] = useState(false);
  const isEditing = !!id;

  const fetchAnexos = useCallback(async () => {
    if (isEditing) {
      try {
        const { data } = await api.get(`/anexos-edital/edital/${id}`);
        setAnexos(data);
      } catch (error) {
        addToast("Erro ao buscar anexos.", "error");
      }
    }
  }, [id, isEditing, addToast]);

  useEffect(() => {
    let isActive = true;

    const toLocalDateTime = (isoString) => {
      if (!isoString) return "";
      const dt = new Date(isoString);
      if (Number.isNaN(dt.getTime())) return "";
      return new Date(dt.getTime() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substring(0, 16);
    };

    const load = async () => {
      setLoadingForm(true);
      try {
        const cargosPromise = api.get("/cargos");
        const editalPromise = isEditing ? api.get(`/editais/${id}`) : null;
        const usersPromise =
          user?.perfil_id === 1 ? api.get("/users") : Promise.resolve({ data: [] });

        const [cargosResponse, usersResponse, editalResponse] =
          await Promise.all([
            cargosPromise,
            usersPromise,
            editalPromise ? editalPromise : Promise.resolve({ data: null }),
          ]);

        if (!isActive) return;

        const cargoOptions = (cargosResponse.data || []).map((cargo) => ({
          value: cargo.id,
          label: cargo.nome_cargo,
        }));
        setTodosCargos(cargoOptions);

        if (user?.perfil_id === 1) {
          const controladorOptions = (usersResponse.data || [])
            .filter((u) => Number(u.perfil_id) === 2)
            .map((u) => ({
              value: u.id,
              label: `${u.nome} ${u.sobrenome || ""}`.trim() + ` (ID ${u.id})`,
            }));
          setControladores(controladorOptions);

          // Garante label completo do controlador ao editar.
          const editalData = editalResponse?.data;
          if (isEditing && editalData?.controlador_id) {
            const found = controladorOptions.find(
              (c) => c.value === editalData.controlador_id
            );
            setControladorSelecionado(
              found || {
                value: editalData.controlador_id,
                label: `Controlador (ID ${editalData.controlador_id})`,
              }
            );
          }
        }

        if (isEditing) {
          const responseData = editalResponse?.data;
          const cargos = Array.isArray(responseData?.cargos)
            ? responseData.cargos
            : [];
          const { cargos: _ignored, ...editalData } = responseData || {};

          setFormData((prev) => ({
            ...prev,
            ...editalData,
            data_inicio_inscricao: toLocalDateTime(editalData.data_inicio_inscricao),
            data_fim_inscricao: toLocalDateTime(editalData.data_fim_inscricao),
            data_inicio_recurso: toLocalDateTime(editalData.data_inicio_recurso),
            data_fim_recurso: toLocalDateTime(editalData.data_fim_recurso),
          }));

          const cargosAtuais = cargos.map((cargo) => {
            const option = cargoOptions.find((c) => c.value === cargo.id);
            return option || { value: cargo.id, label: cargo.nome_cargo };
          });
          setCargosSelecionados(cargosAtuais);

          await fetchAnexos();
        }
      } catch (error) {
        addToast("Erro ao carregar dados do edital.", "error");
      } finally {
        if (isActive) setLoadingForm(false);
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [id, isEditing, fetchAnexos, addToast, user?.perfil_id]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };
  const handleAnexoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
      if (allowedTypes.includes(file.type)) {
        setAnexoFile(file);
      } else {
        addToast(
          "Tipo de arquivo inválido. Por favor, selecione um PDF, JPG ou PNG.",
          "error"
        );
        e.target.value = null; // Limpa o input
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cargoIds = cargosSelecionados.map((c) => c.value);
    if (cargoIds.length === 0) {
      addToast("Selecione pelo menos um cargo para o edital.", "error");
      return;
    }
    const payload = {
      ...formData,
      cargoIds,
      ...(user?.perfil_id === 1
        ? { controlador_id: controladorSelecionado?.value }
        : {}),
      data_inicio_inscricao: new Date(
        formData.data_inicio_inscricao
      ).toISOString(),
      data_fim_inscricao: new Date(formData.data_fim_inscricao).toISOString(),
      data_inicio_recurso: formData.data_inicio_recurso
        ? new Date(formData.data_inicio_recurso).toISOString()
        : null,
      data_fim_recurso: formData.data_fim_recurso
        ? new Date(formData.data_fim_recurso).toISOString()
        : null,
    };
    try {
      setSaving(true);
      if (user?.perfil_id === 1 && !payload.controlador_id) {
        addToast("Selecione um controlador responsável pelo edital.", "error");
        return;
      }
      let editalResponse;
      if (isEditing) {
        editalResponse = await api.put(`/editais/${id}`, payload);
      } else {
        editalResponse = await api.post("/editais", payload, {
          headers: { "Content-Type": "application/json" },
        });
      }
      addToast(isEditing ? "Edital atualizado!" : "Edital criado!", "success");
      if (!isEditing) {
        const basePath =
          user?.perfil_id === 1 ? "/admin/editais" : "/controlador/editais";
        navigate(`${basePath}/editar/${editalResponse.data.id}`);
      }
    } catch (error) {
      addToast("Erro ao salvar edital.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAnexo = async () => {
    if (!anexoFile) return;
    const uploadData = new FormData();
    uploadData.append("file", anexoFile);
    try {
      await api.post(`/anexos-edital/edital/${id}`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      addToast("Anexo adicionado!", "success");
      fetchAnexos();
      setAnexoFile(null);
    } catch (error) {
      addToast("Erro ao adicionar anexo.", "error");
    }
  };

  const handleDeleteAnexo = async (anexoId) => {
    try {
      await api.delete(`/anexos-edital/${anexoId}`);
      addToast("Anexo removido!", "success");
      fetchAnexos();
    } catch (error) {
      addToast("Erro ao remover anexo.", "error");
    }
  };

  return (
    <>
      <PageHeader title={isEditing ? "Editar Edital" : "Criar Novo Edital"}>
        <button
          onClick={() => {
            const path =
              user?.perfil_id === 1 ? "/admin/editais" : "/controlador/editais";
            navigate(path);
          }}
          className="button-secondary"
        >
          <FaArrowLeft />
          <span>Voltar para a Lista</span>
        </button>
      </PageHeader>
      <Card>
        {loadingForm ? (
          <p className="muted-text">Carregando dados do edital...</p>
        ) : (
          <form onSubmit={handleSubmit} className="form-modern">
            <div className="form-section">
              <div className="section-header">
                <h3>Identificação</h3>
                <p>Informações principais do edital.</p>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Nome do Edital</label>
                  <input
                    name="nome_edital"
                    value={formData.nome_edital}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Número</label>
                  <input
                    name="numero_edital"
                    value={formData.numero_edital}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Ano</label>
                  <input
                    name="ano_edital"
                    type="number"
                    value={formData.ano_edital}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label>Descrição</label>
                  <textarea
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h3>Prazos</h3>
                <p>
                  Defina o período de inscrições e a janela de recursos (usada para
                  liberar a abertura de recursos após a pontuação).
                </p>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Início das Inscrições</label>
                  <input
                    name="data_inicio_inscricao"
                    type="datetime-local"
                    value={formData.data_inicio_inscricao}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Fim das Inscrições</label>
                  <input
                    name="data_fim_inscricao"
                    type="datetime-local"
                    value={formData.data_fim_inscricao}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Início do Período de Recurso</label>
                  <input
                    name="data_inicio_recurso"
                    type="datetime-local"
                    value={formData.data_inicio_recurso}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Fim do Período de Recurso</label>
                  <input
                    name="data_fim_recurso"
                    type="datetime-local"
                    value={formData.data_fim_recurso}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h3>Cargos e responsável</h3>
                <p>Selecione os cargos do edital e quem irá analisar as inscrições.</p>
              </div>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Cargos</label>
                  <Select
                    isMulti
                    options={todosCargos}
                    value={cargosSelecionados}
                    onChange={setCargosSelecionados}
                    placeholder="Selecione os cargos para este edital"
                    noOptionsMessage={() =>
                      'Nenhum cargo encontrado. Cadastre um novo na página de "Gerenciar Cargos".'
                    }
                  />
                </div>

                {user?.perfil_id === 1 ? (
                  <div className="form-group full-width">
                    <label>Controlador responsável</label>
                    <Select
                      options={controladores}
                      value={controladorSelecionado}
                      onChange={setControladorSelecionado}
                      placeholder="Selecione o controlador"
                      noOptionsMessage={() => "Nenhum controlador encontrado."}
                    />
                  </div>
                ) : null}

                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="Aberto">Aberto</option>
                    <option value="Em Análise">Em Análise</option>
                    <option value="Finalizado">Finalizado</option>
                  </select>
                </div>

                <div className="form-group form-group-checkbox-toggle">
                  <label className="checkbox-label-inline">
                    <input
                      type="checkbox"
                      name="uma_inscricao_por_cpf"
                      checked={Boolean(formData.uma_inscricao_por_cpf)}
                      onChange={handleInputChange}
                    />
                    <span>
                      Permitir apenas 1 inscrição por CPF neste edital
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="form-actions full-width">
              <button
                type="submit"
                className="btn-modern btn-add"
                disabled={saving}
              >
                <span>{saving ? "Salvando..." : "Salvar Edital"}</span>
              </button>
            </div>
          </form>
        )}
      </Card>
      {isEditing && (
        <Card title="Gerenciar Anexos">
          <div className="upload-controls">
            <div className="upload-actions">
              <label className="btn-modern btn-file">
                <FaFileUpload />
                <span>Escolher arquivo</span>
                <input type="file" onChange={handleAnexoChange} hidden />
              </label>
              <button
                type="button"
                onClick={handleAddAnexo}
                className="btn-modern btn-add"
                disabled={!anexoFile}
              >
                <FaPlus />
                <span>Adicionar</span>
              </button>
            </div>
            {anexoFile && (
              <span className="upload-selected">{anexoFile.name}</span>
            )}
          </div>
          {anexos.length > 0 ? (
            <ul className="attachment-list">
              {anexos.map((anexo) => (
                <li key={anexo.id} className="attachment-item">
                  <a
                    href={`${process.env.REACT_APP_API_URL}/../uploads/${anexo.caminho_arquivo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-name"
                  >
                    {anexo.nome_arquivo}
                  </a>
                  <button
                    onClick={() => handleDeleteAnexo(anexo.id)}
                    className="btn-modern btn-danger button-circle"
                    title="Excluir anexo"
                  >
                    <FaTrash />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted-text">Nenhum anexo cadastrado.</p>
          )}
        </Card>
      )}
    </>
  );
};

export { EditalList, EditalForm };
